import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { apiClient, useAuth } from "../App";
import { toast } from "sonner";
import { usePmTestMode } from "../hooks/usePmTestMode";
import {
  ArrowLeft, Edit2, Trash2, Upload, Download, FileText,
  Save, X, Users, Briefcase, Building2, ExternalLink, Mail, Phone, Globe, Clock, DollarSign, BarChart3
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import WorkloadTab from "../components/resources/WorkloadTab";

const SPECIALTIES = ["Interior Design Lead", "Studio Director", "COO", "Admin", "Director of Marketing", "Asset Management"];
const EMPLOYMENT_TYPES = ["In-house", "Freelancer", "Contractor"];
const PM_TYPES = ["In-house", "Outsourced"];
const STATUSES = ["Active", "On Leave", "Inactive"];
const SERVICE_CATEGORIES = ["Modeling", "Rendering", "Animation", "360 Tours", "Virtual Staging", "Floor Plans"];
const TYPE_LABELS = { ARTIST: "Artist", PROJECT_MANAGER: "Project Manager", STUDIO: "Studio" };

function InfoField({ icon, label, value, isLink }) {
  const Icon = icon;
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 py-2">
      <Icon className="w-4 h-4 text-muted-foreground mt-0.5" />
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        {isLink ? <a href={`mailto:${value}`} className="text-sm text-primary hover:underline">{value}</a> : <p className="text-sm">{value}</p>}
      </div>
    </div>
  );
}

function TagsList({ tags }) {
  if (!tags || tags.length === 0) return null;
  const items = tags.map((t, i) => <Badge key={i} variant="outline" className="text-xs">{t}</Badge>);
  return <div className="flex flex-wrap gap-1 pt-2">{items}</div>;
}

function ServicesList({ categories }) {
  if (!categories || categories.length === 0) return null;
  const items = categories.map((c, i) => <Badge key={i} variant="secondary" className="text-xs">{c}</Badge>);
  return <div className="flex flex-wrap gap-1">{items}</div>;
}

function ContractsList({ contracts, onDownload }) {
  if (!contracts || contracts.length === 0) {
    return <p className="text-sm text-muted-foreground text-center py-8">No contracts uploaded yet</p>;
  }
  const items = contracts.map(c => (
    <div key={c.id} className="flex items-center justify-between p-3 border rounded-lg">
      <div className="flex items-center gap-3 min-w-0">
        <FileText className="w-5 h-5 text-muted-foreground flex-shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{c.file_name}</p>
          <p className="text-xs text-muted-foreground">v{c.version} - {new Date(c.uploaded_at).toLocaleDateString()}</p>
        </div>
        {c.is_active && <Badge variant="secondary" className="text-xs">Active</Badge>}
      </div>
      <Button variant="ghost" size="sm" onClick={() => onDownload(c)}><Download className="w-4 h-4" /></Button>
    </div>
  ));
  return <div className="space-y-2">{items}</div>;
}

function AssignmentsList({ assignments, onNavigate }) {
  if (!assignments || assignments.length === 0) {
    return <p className="text-sm text-muted-foreground text-center py-8">Not assigned to any programs</p>;
  }
  const items = assignments.map(a => (
    <div key={a.id} className="flex items-center justify-between p-3 border rounded-lg cursor-pointer hover:bg-secondary/30" onClick={() => onNavigate(a.program_id)}>
      <div className="min-w-0">
        <p className="text-sm font-medium">{a.program_name}</p>
        <div className="flex gap-2 mt-1">
          <Badge variant="outline" className="text-xs">{a.role_on_program}</Badge>
          {a.can_view_financials && <Badge variant="secondary" className="text-xs">Financials</Badge>}
          {a.can_edit_program && <Badge variant="secondary" className="text-xs">Edit</Badge>}
        </div>
      </div>
      <ExternalLink className="w-4 h-4 text-muted-foreground" />
    </div>
  ));
  return <div className="space-y-2">{items}</div>;
}

function StatusOptions() {
  const items = STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>);
  return <>{items}</>;
}

function SpecialtyOptions() {
  const items = SPECIALTIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>);
  return <>{items}</>;
}

function EmploymentOptions() {
  const items = EMPLOYMENT_TYPES.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>);
  return <>{items}</>;
}

function PMTypeOptions() {
  const items = PM_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>);
  return <>{items}</>;
}

function ServiceButtons({ selected, onToggle }) {
  const items = SERVICE_CATEGORIES.map(cat => (
    <Button key={cat} type="button" variant={selected?.includes(cat) ? "default" : "outline"} size="sm" onClick={() => onToggle(cat)} className="text-xs">{cat}</Button>
  ));
  return <div className="flex flex-wrap gap-2">{items}</div>;
}

export default function ResourceProfilePage() {
  const { resourceId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  // Get initial tab from URL query param
  const initialTab = searchParams.get("tab") || "profile";
  
  const [resource, setResource] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [uploadingContract, setUploadingContract] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [contractNotes, setContractNotes] = useState("");

  const isNew = resourceId === "new" || resourceId === undefined;
  const isCOO = user?.role === "COO";
  const pmTestMode = usePmTestMode();
  const isPM = user?.role === "Program Manager" || user?.role === "Marketing PM" || pmTestMode?.isActive;
  const canViewCompensation = !isPM && ["COO", "Admin", "Accountant"].includes(user?.role);

  useEffect(() => {
    if (isNew) {
      const params = new URLSearchParams(window.location.search);
      const type = params.get("type") || "ARTIST";
      setForm({
        resource_type: type,
        display_name: "",
        status: "Active",
        timezone: "America/New_York",
        tags: [],
        working_days: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        default_hours_per_day: 8,
        service_categories: [],
        default_can_view_financials: true
      });
      setEditing(true);
      setLoading(false);
    } else {
      fetchResource();
      fetchContracts();
    }
  }, [resourceId]);

  async function fetchResource() {
    try {
      const response = await apiClient.get(`/resources/${resourceId}`);
      setResource(response.data);
      setForm(response.data);
    } catch (err) {
      toast.error("Failed to load resource");
      navigate("/studio/resources");
    } finally {
      setLoading(false);
    }
  }

  async function fetchContracts() {
    try {
      const response = await apiClient.get(`/resources/${resourceId}/contracts`);
      setContracts(response.data);
    } catch (err) {
      // ignore
    }
  }

  async function handleSave() {
    if (!form.display_name?.trim()) {
      toast.error("Display name is required");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const response = await apiClient.post("/resources", form);
        toast.success("Resource created");
        navigate(`/studio/resources/${response.data.id}`);
      } else {
        await apiClient.patch(`/resources/${resourceId}`, form);
        toast.success("Resource updated");
        setResource({ ...resource, ...form });
        setEditing(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!confirm("Are you sure you want to delete this resource?")) return;
    try {
      await apiClient.delete(`/resources/${resourceId}`);
      toast.success("Resource deleted");
      navigate("/studio/resources");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  }

  async function handleUploadContract(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingContract(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      if (contractNotes) formData.append("notes", contractNotes);
      await apiClient.post(`/resources/${resourceId}/contracts`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Contract uploaded");
      fetchContracts();
      setShowUploadDialog(false);
      setContractNotes("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingContract(false);
    }
  }

  async function handleDownloadContract(contract) {
    try {
      const response = await apiClient.get(
        `/resources/${resourceId}/contracts/${contract.id}/download`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = contract.file_name;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Download failed");
    }
  }

  function handleTagsChange(tagsStr) {
    const tags = tagsStr.split(",").map(t => t.trim()).filter(Boolean);
    setForm({ ...form, tags });
  }

  function toggleServiceCategory(cat) {
    const current = form.service_categories || [];
    setForm({
      ...form,
      service_categories: current.includes(cat) ? current.filter(c => c !== cat) : [...current, cat]
    });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const typeLabel = TYPE_LABELS[form.resource_type] || "Resource";
  const TypeIcon = form.resource_type === "PROJECT_MANAGER" ? Briefcase : form.resource_type === "STUDIO" ? Building2 : Users;

  return (
    <div data-testid="resource-detail" className="max-w-4xl mx-auto pb-20 md:pb-0">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate("/studio/resources")}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <TypeIcon className="w-5 h-5 text-muted-foreground" />
            <h1 className="text-lg sm:text-2xl font-light truncate" style={{ fontFamily: 'var(--font-sans)' }}>
              {isNew ? `New ${typeLabel}` : form.display_name}
            </h1>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground">{typeLabel}</p>
        </div>
        {!isNew && !editing && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              <Edit2 className="w-4 h-4 mr-1" /> Edit
            </Button>
            {isCOO && (
              <Button variant="outline" size="sm" onClick={handleDelete} className="text-red-600 hover:text-red-700">
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        )}
        {editing && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => (isNew ? navigate(-1) : setEditing(false))}>
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving}>
              <Save className="w-4 h-4 mr-1" /> {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        )}
      </div>

      <Tabs defaultValue={initialTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="profile">Profile</TabsTrigger>
          {!isNew && <TabsTrigger value="workload">Workload</TabsTrigger>}
          {!isNew && <TabsTrigger value="contracts">Contracts</TabsTrigger>}
          {!isNew && <TabsTrigger value="assignments">Assignments</TabsTrigger>}
        </TabsList>

        <TabsContent value="profile">
          <div className="grid md:grid-cols-2 gap-4">
            {/* Basic Info Card */}
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base">Basic Information</CardTitle></CardHeader>
              <CardContent>
                {editing ? (
                  <div className="space-y-4">
                    <div className="space-y-1.5">
                      <Label>Display Name *</Label>
                      <Input value={form.display_name || ""} onChange={e => setForm({ ...form, display_name: e.target.value })} placeholder="How they're referred to" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Legal Name</Label>
                      <Input value={form.legal_name || ""} onChange={e => setForm({ ...form, legal_name: e.target.value })} placeholder="Full legal name" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label>Email</Label>
                        <Input type="email" value={form.email || ""} onChange={e => setForm({ ...form, email: e.target.value })} />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Phone</Label>
                        <Input value={form.phone || ""} onChange={e => setForm({ ...form, phone: e.target.value })} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label>Status</Label>
                        <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent><StatusOptions /></SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label>Timezone</Label>
                        <Input value={form.timezone || ""} onChange={e => setForm({ ...form, timezone: e.target.value })} />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Tags (comma separated)</Label>
                      <Input value={form.tags?.join(", ") || ""} onChange={e => handleTagsChange(e.target.value)} placeholder="Luxury, Fast Turnaround, Interiors" />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <InfoField icon={Users} label="Display Name" value={form.display_name} />
                    <InfoField icon={Users} label="Legal Name" value={form.legal_name} />
                    <InfoField icon={Mail} label="Email" value={form.email} isLink />
                    <InfoField icon={Phone} label="Phone" value={form.phone} />
                    <InfoField icon={Globe} label="Timezone" value={form.timezone} />
                    <div className="pt-2"><Badge variant={form.status === "Active" ? "default" : "secondary"}>{form.status}</Badge></div>
                    <TagsList tags={form.tags} />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Type-Specific Details Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">
                  {form.resource_type === "ARTIST" ? "Artist Details" : form.resource_type === "PROJECT_MANAGER" ? "PM Details" : "Studio Details"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {form.resource_type === "ARTIST" && (
                  editing ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label>Specialty</Label>
                          <Select value={form.specialty || ""} onValueChange={v => setForm({ ...form, specialty: v })}>
                            <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent><SpecialtyOptions /></SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1.5">
                          <Label>Employment</Label>
                          <Select value={form.employment_type || ""} onValueChange={v => setForm({ ...form, employment_type: v })}>
                            <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                            <SelectContent><EmploymentOptions /></SelectContent>
                          </Select>
                        </div>
                      </div>
                      {canViewCompensation && (
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1.5">
                            <Label>Wage Type</Label>
                            <Select value={form.wage_type || ""} onValueChange={v => setForm({ ...form, wage_type: v })}>
                              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Hourly">Hourly</SelectItem>
                                <SelectItem value="Daily">Daily</SelectItem>
                                <SelectItem value="Monthly">Monthly</SelectItem>
                                <SelectItem value="Per Project">Per Project</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1.5">
                            <Label>Rate ({form.currency || "USD"})</Label>
                            <Input type="number" value={form.wage_amount || ""} onChange={e => setForm({ ...form, wage_amount: parseFloat(e.target.value) || null })} />
                          </div>
                        </div>
                      )}
                      <div className="space-y-1.5">
                        <Label>Hours/Day</Label>
                        <Input type="number" value={form.default_hours_per_day || 8} onChange={e => setForm({ ...form, default_hours_per_day: parseFloat(e.target.value) || 8 })} />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <InfoField icon={Briefcase} label="Specialty" value={form.specialty} />
                      <InfoField icon={Users} label="Employment" value={form.employment_type} />
                      {canViewCompensation && form.wage_type && <InfoField icon={DollarSign} label="Rate" value={`${form.wage_amount} ${form.currency || "USD"} (${form.wage_type})`} />}
                      <InfoField icon={Clock} label="Hours/Day" value={form.default_hours_per_day} />
                    </div>
                  )
                )}
                {form.resource_type === "PROJECT_MANAGER" && (
                  editing ? (
                    <div className="space-y-4">
                      <div className="space-y-1.5">
                        <Label>PM Type</Label>
                        <Select value={form.pm_type || ""} onValueChange={v => setForm({ ...form, pm_type: v })}>
                          <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent><PMTypeOptions /></SelectContent>
                        </Select>
                      </div>
                      {canViewCompensation && (
                        <div className="space-y-1.5">
                          <Label>Hourly Rate</Label>
                          <Input type="number" value={form.hourly_rate || ""} onChange={e => setForm({ ...form, hourly_rate: parseFloat(e.target.value) || null })} />
                        </div>
                      )}
                      {canViewCompensation && (
                        <div className="flex items-center gap-2 pt-2">
                          <input type="checkbox" checked={form.default_can_view_financials !== false} onChange={e => setForm({ ...form, default_can_view_financials: e.target.checked })} className="rounded" />
                          <Label className="text-sm font-normal">Can view financials by default</Label>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <InfoField icon={Briefcase} label="PM Type" value={form.pm_type} />
                      {canViewCompensation && form.hourly_rate && <InfoField icon={DollarSign} label="Hourly Rate" value={`$${form.hourly_rate}`} />}
                      {canViewCompensation && <div className="pt-2"><Badge variant={form.default_can_view_financials !== false ? "default" : "secondary"}>{form.default_can_view_financials !== false ? "Can View Financials" : "No Financial Access"}</Badge></div>}
                    </div>
                  )
                )}
                {form.resource_type === "STUDIO" && (
                  editing ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label>Contact Name</Label>
                          <Input value={form.primary_contact_name || ""} onChange={e => setForm({ ...form, primary_contact_name: e.target.value })} />
                        </div>
                        <div className="space-y-1.5">
                          <Label>Contact Email</Label>
                          <Input type="email" value={form.primary_contact_email || ""} onChange={e => setForm({ ...form, primary_contact_email: e.target.value })} />
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label>Services</Label>
                        <ServiceButtons selected={form.service_categories} onToggle={toggleServiceCategory} />
                      </div>
                      {isCOO && (
                        <div className="space-y-1.5">
                          <Label>Billing Terms</Label>
                          <Textarea value={form.billing_terms || ""} onChange={e => setForm({ ...form, billing_terms: e.target.value })} rows={2} />
                        </div>
                      )}
                      <div className="space-y-1.5">
                        <Label>Capacity Notes</Label>
                        <Textarea value={form.studio_capacity_notes || ""} onChange={e => setForm({ ...form, studio_capacity_notes: e.target.value })} rows={2} />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <InfoField icon={Users} label="Primary Contact" value={form.primary_contact_name} />
                      <InfoField icon={Mail} label="Contact Email" value={form.primary_contact_email} isLink />
                      {form.service_categories?.length > 0 && <div className="pt-2"><p className="text-xs text-muted-foreground mb-1">Services</p><ServicesList categories={form.service_categories} /></div>}
                      {form.studio_capacity_notes && <div className="pt-2"><p className="text-xs text-muted-foreground">Capacity Notes</p><p className="text-sm">{form.studio_capacity_notes}</p></div>}
                    </div>
                  )
                )}
              </CardContent>
            </Card>

            {/* Notes Card */}
            <Card className="md:col-span-2">
              <CardHeader className="pb-3"><CardTitle className="text-base">Notes</CardTitle></CardHeader>
              <CardContent>
                {editing ? (
                  <Textarea value={form.notes || ""} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Internal notes..." rows={3} />
                ) : (
                  <p className="text-sm text-muted-foreground">{form.notes || "No notes"}</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {!isNew && (
          <TabsContent value="workload">
            <WorkloadTab resourceId={resourceId} resourceName={form.display_name} />
          </TabsContent>
        )}

        {!isNew && (
          <TabsContent value="contracts">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div><CardTitle className="text-base">Contract Documents</CardTitle><CardDescription>PDF contracts and agreements</CardDescription></div>
                <Button size="sm" onClick={() => setShowUploadDialog(true)}><Upload className="w-4 h-4 mr-2" /> Upload</Button>
              </CardHeader>
              <CardContent>
                <ContractsList contracts={contracts} onDownload={handleDownloadContract} />
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {!isNew && (
          <TabsContent value="assignments">
            <Card>
              <CardHeader><CardTitle className="text-base">Program Assignments</CardTitle><CardDescription>Programs this resource is assigned to</CardDescription></CardHeader>
              <CardContent>
                <AssignmentsList assignments={resource?.assignments} onNavigate={(id) => navigate(`/programs/${id}`)} />
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Upload Contract</DialogTitle><DialogDescription>Upload a PDF contract document</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Notes (optional)</Label>
              <Input value={contractNotes} onChange={e => setContractNotes(e.target.value)} placeholder="e.g., 2024 renewal" />
            </div>
            <div className="border-2 border-dashed rounded-lg p-6 text-center">
              <input type="file" accept=".pdf" onChange={handleUploadContract} className="hidden" id="contract-upload" disabled={uploadingContract} />
              <label htmlFor="contract-upload" className="cursor-pointer">
                <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm">{uploadingContract ? "Uploading..." : "Click to select PDF"}</p>
              </label>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
